// src/pages/accountpage/AccountsDashboard.js
import React, { useEffect, useMemo, useState, useContext } from 'react';
import api from '../../api';
import { Modal, Button as BsButton, Form, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { PageHeader, StatCardGrid, StatCard, Button, SearchInput, FilterSelect, FilterToolbar, StatusBadge, Pagination, ErrorState, LoadingState } from '../../components/ui';
import { FaSyncAlt, FaPlus, FaClock, FaSpinner, FaCheckCircle } from 'react-icons/fa';

const ROWS_PER_PAGE = 5;

const todayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default function AccountsDashboard() {
  const { user } = useContext(AuthContext);

  /* ---------- axios (auth) ---------- */
  const token = localStorage.getItem('token') || '';
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  /* ---------- state ---------- */
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | pending | in_progress | completed
  const [page, setPage] = useState(1);

  // create modal
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category_id: '', sub_category_id: '', task_date: todayDateString() });

  // remark modal
  const [showRemark, setShowRemark] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);

  // toasts
  const [toast, setToast] = useState({ show: false, msg: '', variant: 'success' });
  const pop = (msg, variant = 'success') => setToast({ show: true, msg, variant });

  /* ---------- fetchers ---------- */
  const fetchTasks = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/tasks/user/${user.id}`, { headers });
      setTasks(Array.isArray(data) ? data : []);
      setPage(1);
    } catch (e) {
      setTasks([]);
      setError(`Failed to load tasks (${e?.response?.status || 'ERR'})`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!user?.unit_id) { setCategories([]); return; }
    try {
      const { data } = await api.get(
        `/categories/by-unit/${user.unit_id}`,
        { headers }
      );
      setCategories(Array.isArray(data) ? data : []);
    } catch { setCategories([]); }
  };

  const fetchSubcategories = async (catId) => {
    if (!catId || !user?.unit_id) { setSubcategories([]); return; }
    try {
      const { data } = await api.get(
        `/subcategories/by-category/${catId}/unit/${user.unit_id}`,
        { headers }
      );
      setSubcategories(Array.isArray(data) ? data : []);
    } catch { setSubcategories([]); }
  };

  /* ---------- effects ---------- */
  useEffect(() => { if (user?.id) fetchTasks(); }, [user?.id]);
  useEffect(() => { fetchCategories(); }, [user?.unit_id]);
  useEffect(() => { fetchSubcategories(form.category_id); }, [form.category_id, user?.unit_id]);

  /* ---------- Estimated TAT --
   * Read-only display of the Subcategory's own configured tat_value/
   * tat_unit (services/subcategoryTatService.js is the single source of
   * truth). Never editable here and never sent in the payload -- the
   * backend independently resolves TAT from sub_category_id alone. ---------- */
  const selectedSubcategory = subcategories.find((s) => String(s.id) === String(form.sub_category_id));
  const subcategoryHasTat = !!(selectedSubcategory && selectedSubcategory.tat_value != null && selectedSubcategory.tat_unit);

  /* ---------- create ---------- */
  const createTask = async () => {
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category_id: Number(form.category_id),
      sub_category_id: Number(form.sub_category_id),
      created_by: user.id,
      unit_id: user.unit_id,
      task_date: form.task_date,
    };
    if (
      !payload.title || !payload.description || !payload.category_id || !payload.sub_category_id || !form.task_date
    ) return;

    setSaving(true);
    setError('');
    try {
      // Your backend /tasks/staff already emails technician + creator (best-effort)
      const { data } = await api.post(`/tasks/staff`, payload, { headers });
      setShowCreate(false);
      setForm({ title: '', description: '', category_id: '', sub_category_id: '', task_date: todayDateString() });
      await fetchTasks();
      const assignedTxt = data?.assigned_to ? `Assigned to user #${data.assigned_to}` : 'Queued for assignment';
      pop(`Task created. ${assignedTxt}. Emails sent.`, 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || 'Failed to create task';
      setError(msg);
      pop(msg, 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- remarks ---------- */
  const nextRemarkSlot = (t) => {
    if (!t?.remark_1) return 1;
    if (!t?.remark_2) return 2;
    if (!t?.remark_3) return 3;
    return null;
  };
  const openRemark = (t) => { setActiveTask(t); setRemarkText(''); setShowRemark(true); };

  const addRemark = async () => {
    if (!activeTask) return;
    const text = remarkText.trim();
    if (!text) return;

    setRemarkSaving(true);
    setError('');
    try {
      const { data } = await api.post(
        `/tasks/${activeTask.id}/remarks`,
        { remark: text, remark_type: 'general', created_by: user.id },
        { headers }
      );
      if (data?.id) setTasks(prev => prev.map(t => (t.id === data.id ? data : t)));
      setShowRemark(false);
      setRemarkText('');
      pop('Remark added and notification sent.', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to add remark';
      setError(msg);
      pop(msg, 'danger');
    } finally {
      setRemarkSaving(false);
    }
  };

  /* ---------- derived ---------- */
  const fmt = (dt) => (dt ? new Date(dt).toLocaleString() : '');
  // StatusBadge (src/components/ui) reuses the shared status color mapping
  // (StatusChips.jsx), which keys on "in_progress" (underscore) -- this
  // page's own status value is written with a space ("in progress"), so it
  // is normalized purely for color-matching purposes here. This never
  // changes the underlying value that's read from/sent to the API, only
  // which color badge gets shown for it.
  const normalizedStatus = (s) => String(s || '').toLowerCase().replace(/\s+/g, '_');

  const counts = useMemo(() => {
    const c = { pending: 0, in_progress: 0, completed: 0 };
    tasks.forEach(t => { const k = (t.status || '').toLowerCase().replace(' ', '_'); if (k in c) c[k]++; });
    return c;
  }, [tasks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter(t => {
      const passStatus = statusFilter === 'ALL' ? true : (t.status || '').toLowerCase() === statusFilter;
      if (!passStatus) return false;
      if (!q) return true;
      return [t.title, t.description, t.status, t.category_name, t.sub_category_name, t.assigned_to_name]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(q));
    });
  }, [tasks, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const pageRows = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  /* ---------- UI ---------- */
  return (
    <div className="container-fluid py-3" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        eyebrow="Accounts"
        title="Accounts Dashboard"
        description="Track and manage tasks assigned to you."
        actions={(
          <>
            <Button variant="secondary" icon={<FaSyncAlt />} onClick={fetchTasks}>Refresh</Button>
            <Button variant="primary" icon={<FaPlus />} onClick={() => setShowCreate(true)}>Add Task</Button>
          </>
        )}
      />

      <StatCardGrid>
        <StatCard icon={<FaClock />} label="Pending" value={counts.pending} tone="warning" />
        <StatCard icon={<FaSpinner />} label="In Progress" value={counts.in_progress} tone="info" />
        <StatCard icon={<FaCheckCircle />} label="Completed" value={counts.completed} tone="success" />
      </StatCardGrid>

      <FilterToolbar>
        <SearchInput
          placeholder="Search my tasks"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <FilterSelect
          allLabel="All statuses"
          value={statusFilter === 'ALL' ? '' : statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value || 'ALL'); setPage(1); }}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'in progress', label: 'In progress' },
            { value: 'completed', label: 'Completed' },
          ]}
        />
      </FilterToolbar>

      {error && <ErrorState title="Couldn't load tasks" message={error} />}
      {loading && <LoadingState message="Loading tasks…" />}

      {!error && !loading && (
        <div className="ui-card">
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sno.</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Assignee</th>
                  <th>Category</th>
                  <th>Sub-category</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Created At</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center p-4">
                      <div className="text-muted">No matching tasks</div>
                    </td>
                  </tr>
                ) : (
                  pageRows.map((t, i) => (
                    <tr key={t.id}>
                      <td>{(page - 1) * ROWS_PER_PAGE + i + 1}</td>
                      <td className="fw-semibold">{t.title}</td>
                      <td className="text-muted">{t.description}</td>
                      <td><span className="badge text-bg-light">{t.assigned_to_name || '—'}</span></td>
                      <td>{t.category_name || '—'}</td>
                      <td>{t.sub_category_name || '—'}</td>
                      <td><StatusBadge status={normalizedStatus(t.status)} /></td>
                      <td style={{ minWidth: 320 }}>
                        <div className="d-flex flex-column gap-1">
                          {t.remark_1 && (
                            <span className="badge text-dark" style={{ background: '#e9ecef' }}>
                              <b>R1:</b> {t.remark_1}
                              <small className="text-muted ms-2">{fmt(t.remark_1_at)}</small>
                            </span>
                          )}
                          {t.remark_2 && (
                            <span className="badge text-dark" style={{ background: '#e9ecef' }}>
                              <b>R2:</b> {t.remark_2}
                              <small className="text-muted ms-2">{fmt(t.remark_2_at)}</small>
                            </span>
                          )}
                          {t.remark_3 && (
                            <span className="badge text-dark" style={{ background: '#e9ecef' }}>
                              <b>R3:</b> {t.remark_3}
                              <small className="text-muted ms-2">{fmt(t.remark_3_at)}</small>
                            </span>
                          )}
                          {!t.remark_1 && <span className="text-muted">No remarks yet</span>}
                        </div>
                      </td>
                      <td>{fmt(t.created_at)}</td>
                      <td>
                        {(t.status || '').toLowerCase() === 'in progress' && nextRemarkSlot(t) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openRemark(t)}
                            title={`Add Remark ${nextRemarkSlot(t)}`}
                          >
                            + Remark {nextRemarkSlot(t)}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} pageSize={ROWS_PER_PAGE} onPageChange={setPage} />
        </div>
      )}

      {/* Create Task */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} style={{ marginTop: '82px' }}>
        <Modal.Header closeButton>
          <Modal.Title>Create Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Short title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the issue/request…"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value, sub_category_id: '' })}
            >
              <option value="">-- Select --</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Sub-category</Form.Label>
            <Form.Select
              value={form.sub_category_id}
              disabled={!form.category_id}
              onChange={(e) => setForm({ ...form, sub_category_id: e.target.value })}
            >
              <option value="">-- Select --</option>
              {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Form.Select>
            <Form.Text className="text-muted d-block mt-2">
              Technician assignment is automatic based on unit & sub-category workload.
            </Form.Text>
          </Form.Group>

          {form.sub_category_id && (
            <Form.Group className="mt-3">
              <Form.Label>Estimated TAT</Form.Label>
              {subcategoryHasTat ? (
                <Form.Control readOnly value={`${selectedSubcategory.tat_value} ${selectedSubcategory.tat_unit}`} />
              ) : (
                <Form.Text className="text-muted d-block">
                  No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Task Date *</Form.Label>
            <Form.Control
              type="date"
              max={todayDateString()}
              value={form.task_date}
              onChange={(e) => setForm({ ...form, task_date: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" onClick={() => setShowCreate(false)}>Cancel</BsButton>
          <BsButton
            variant="primary"
            onClick={createTask}
            disabled={
              saving ||
              !form.title.trim() ||
              !form.description.trim() ||
              !form.category_id ||
              !form.sub_category_id ||
              !form.task_date
            }
          >
            {saving ? <Spinner size="sm" /> : 'Create'}
          </BsButton>
        </Modal.Footer>
      </Modal>

      {/* Add Remark */}
      <Modal show={showRemark} onHide={() => setShowRemark(false)} style={{ marginTop: '82px' }}>
        <Modal.Header closeButton>
          <Modal.Title>
            Add Remark {activeTask ? `for "${activeTask.title}"` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              placeholder="Type a short progress note…"
            />
            <Form.Text muted>Max 255 characters. You can add up to 3 remarks.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" onClick={() => setShowRemark(false)}>Cancel</BsButton>
          <BsButton variant="primary" disabled={!remarkText.trim() || remarkSaving} onClick={addRemark}>
            {remarkSaving ? <Spinner size="sm" /> : 'Save Remark'}
          </BsButton>
        </Modal.Footer>
      </Modal>

      {/* Toasts */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg={toast.variant} onClose={() => setToast({ ...toast, show: false })} show={toast.show} delay={3200} autohide>
          <Toast.Body className="text-white">{toast.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
